import { Fragment, memo } from 'react'
import SpeakerLabel from './SpeakerLabel'
import { splitSentences } from '@/utils/sentenceLines'
import { formatElapsedRange, formatElapsedTime } from '@/utils/formatElapsedTime'
import { TRANSCRIPT_TIMINGS_ENABLED } from '@/constants/feature-flags'
import type { RealtimeToken } from '@soniox/client'

/**
 * Synthetic token text marking a hard line break with no visible content —
 * used when a mid-recording language change restarts the STT socket, so the
 * archived tokens before the restart never run into the new stream's tokens
 * with no space between them (`buildTokenRows` forces the row after this one
 * to start a new line, matching the same-speaker/same-language case that
 * would otherwise render with no break).
 */
export const LANGUAGE_CHANGE_BREAK_TOKEN_TEXT = '<lang-break>'

export interface TokenRowProps {
  token: RealtimeToken
  isNewSpeaker: boolean
  isNewLanguage: boolean
  /** Previous token closed a sentence, so this one opens a fresh line (see `buildTokenRows`). */
  startsNewLine: boolean
  finalTokenClass: string
  draftTokenClass: string
  /** Show an elapsed-time marker (`token.start_ms`) on the new-speaker label. */
  showTimestamp?: boolean
  /**
   * This pane tints its newest finalized utterance (Output pane only).
   *
   * Never applied to drafts: those arrive several times a second, and tinting
   * each one would strobe — the reason a per-token highlight was kept out of
   * these panes in the first place.
   */
  highlightOnArrival?: boolean
  /**
   * First sentence of this token that belongs to the pane's newest utterance,
   * and so wears the tint along with every sentence after it. Undefined when
   * none of them do.
   *
   * An index rather than a flag because the tint may open partway into a
   * segment: where an utterance begins mid-segment, the sentences that closed
   * before it are older. Decided by `buildTokenRows`, the only place that can
   * see the neighbouring tokens.
   */
  highlightFromSentenceIndex?: number
  /** This finalized segment is a same-speaker continuation at least 30s past the last shown marker — render a standalone timestamp + line break (see `buildTokenRows`). */
  showsPeriodicTimestamp?: boolean
}

interface NamedSpeakerToken extends RealtimeToken {
  speaker_name?: string
}

/** End of the segment, carried by the proxy path beside the vendor's `start_ms`. */
interface TimedToken extends RealtimeToken {
  end_ms?: number
}

/**
 * Renders a single transcript token (speaker label, language break, then
 * text). Wrapped in `React.memo` and kept in its own module (rather than
 * inlined in `Renderer.tsx`) so appending a new token to a long-running
 * transcript only mounts the new row instead of re-rendering every earlier
 * row on each update. Each sentence span plays a one-off CSS fade-in
 * (`animate-stream-in`) on mount — it never replays afterwards since the
 * same span is reused (not remounted) while a draft token's text keeps growing.
 *
 * Text is laid out one sentence per line. The panes render tokens inline with no separator, so
 * without this a long turn arrives as a single unreadable paragraph. Breaks are visual only —
 * the token text is never modified, apart from trimming the leading space off a line so lines
 * do not start indented (the backend's `format_segment` prefixes every segment with one).
 */
export const TokenRow = memo(function TokenRow({
  token,
  isNewSpeaker,
  isNewLanguage,
  startsNewLine,
  finalTokenClass,
  draftTokenClass,
  showTimestamp,
  showsPeriodicTimestamp,
  highlightOnArrival,
  highlightFromSentenceIndex,
}: TokenRowProps) {
  if (token.text === '<end>') {
    return <span className="text-xs italic text-gray-400">{` <end>`}</span>
  }

  if (token.text === LANGUAGE_CHANGE_BREAK_TOKEN_TEXT) {
    return null
  }

  const sentences = splitSentences(token.text)
  // A speaker label is a block element and a language break already emits a <br>, so either one
  // has opened the line for us — adding another break here would leave a blank line.
  const needsLeadingBreak = startsNewLine && !isNewSpeaker && !isNewLanguage
  const tokenClass = token.is_final ? finalTokenClass : draftTokenClass
  // The arrival fade-in stays on the span for its whole life. The highlight used
  // to replace it, which was safe only while the highlight was itself permanent
  // — with the tint moving from one sentence to the next, swapping classes would
  // hand `animate-stream-in` back to old text and replay the fade-in on it every
  // time a newer sentence took the tint away. So the tint rides on its own
  // classes and moves `background-color` alone, a property this one never
  // touches (see `index.css`).
  const tintable = Boolean(highlightOnArrival) && token.is_final
  // Every tintable sentence keeps the class that carries the transition, lit or
  // not, so the colour has something to fade out through when the tint moves on.
  const firstTintedSentence = highlightFromSentenceIndex ?? Number.POSITIVE_INFINITY
  // The SpeakerLabel branch below already carries the timestamp when the
  // speaker changes — this is the periodic (same-speaker, 30s+) case: a bare
  // marker for a finalized segment that doesn't get a repeated speaker name.
  //
  // With timings on, that marker instead appears on EVERY finalized segment and
  // carries the span rather than the start, so a row in Input has a row in Output
  // to line up against. The speaker label then stops printing its own start, or
  // the two would sit side by side saying nearly the same thing.
  const showsStandaloneTimestamp =
    token.start_ms !== undefined &&
    (TRANSCRIPT_TIMINGS_ENABLED ? token.is_final : Boolean(showsPeriodicTimestamp))
  const labelTimestampMs =
    !TRANSCRIPT_TIMINGS_ENABLED && showTimestamp && token.is_final ? token.start_ms : undefined

  return (
    <>
      {isNewSpeaker && token.speaker && (
        <SpeakerLabel
          speakerNumber={token.speaker}
          speakerName={(token as NamedSpeakerToken).speaker_name}
          timestampMs={labelTimestampMs}
        />
      )}
      {isNewLanguage && !isNewSpeaker && <br />}
      {needsLeadingBreak && <br />}
      {showsStandaloneTimestamp && (
        <time className="mr-2 whitespace-nowrap text-xs tabular-nums text-slate-500 dark:text-[var(--text-tertiary)]">
          {TRANSCRIPT_TIMINGS_ENABLED
            ? formatElapsedRange(token.start_ms as number, (token as TimedToken).end_ms)
            : formatElapsedTime(token.start_ms as number)}
        </time>
      )}
      {sentences.map((sentence, index) => {
        const opensLine =
          index > 0 || needsLeadingBreak || isNewSpeaker || isNewLanguage || showsStandaloneTimestamp
        return (
          <Fragment key={index}>
            {index > 0 && <br />}
            <span
              className={[
                tokenClass,
                'animate-stream-in',
                tintable ? 'output-highlight' : '',
                tintable && index >= firstTintedSentence ? 'output-highlight-on' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {opensLine ? sentence.replace(/^\s+/, '') : sentence}
            </span>
          </Fragment>
        )
      })}
    </>
  )
})
